import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, ArrowLeft, MessageSquare, Check, CheckCheck, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { authHeaders, useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

function TimeAgo({ date }) {
  if (!date) return null;
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return <span>now</span>;
  if (mins < 60) return <span>{mins}m</span>;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return <span>{hrs}h</span>;
  const days = Math.floor(hrs / 24);
  return <span>{days}d</span>;
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [pending, setPending] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('messages'); // messages, connections, requests
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const [convRes, connRes, pendRes] = await Promise.all([
        fetch(`${API}/conversations`, { headers: authHeaders() }),
        fetch(`${API}/connections`, { headers: authHeaders() }),
        fetch(`${API}/connections/pending`, { headers: authHeaders() }),
      ]);
      if (convRes.ok) setConversations(await convRes.json());
      if (connRes.ok) setConnections(await connRes.json());
      if (pendRes.ok) setPending(await pendRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Poll for new messages when in an active conversation
  useEffect(() => {
    if (!activeConvo) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/conversations/${activeConvo.conversation_id}/messages?limit=50`, { headers: authHeaders() });
        if (res.ok) setMessages(await res.json());
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeConvo]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    try {
      const res = await fetch(`${API}/conversations/${convo.conversation_id}/messages?limit=50`, { headers: authHeaders() });
      if (res.ok) setMessages(await res.json());
      // Mark read
      await fetch(`${API}/conversations/${convo.conversation_id}/read`, { method: 'PUT', headers: authHeaders() });
      // Update unread in list
      setConversations(prev => prev.map(c => c.conversation_id === convo.conversation_id ? { ...c, unread_count: 0 } : c));
    } catch {}
  };

  const startChat = async (connectionUserId) => {
    try {
      const res = await fetch(`${API}/conversations/${connectionUserId}`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const convo = await res.json();
        await fetchConversations();
        setActiveConvo({ ...convo, partner_name: connections.find(c => c.user_id === connectionUserId)?.name || '' });
        const msgRes = await fetch(`${API}/conversations/${convo.conversation_id}/messages?limit=50`, { headers: authHeaders() });
        if (msgRes.ok) setMessages(await msgRes.json());
        setTab('messages');
      }
    } catch {}
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeConvo || sending) return;
    const content = input.trim();
    setInput('');
    setSending(true);
    // Optimistic update
    const tempMsg = { message_id: `temp_${Date.now()}`, sender_id: user.user_id, content, created_at: new Date().toISOString(), read: false };
    setMessages(prev => [...prev, tempMsg]);
    try {
      const res = await fetch(`${API}/conversations/${activeConvo.conversation_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => prev.map(m => m.message_id === tempMsg.message_id ? msg : m));
        setConversations(prev => prev.map(c =>
          c.conversation_id === activeConvo.conversation_id
            ? { ...c, last_message: content, last_message_at: msg.created_at, last_sender_id: user.user_id }
            : c
        ));
      }
    } catch {}
    setSending(false);
  };

  const respondToRequest = async (connId, status) => {
    try {
      await fetch(`${API}/connections/${connId}/respond`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      setPending(prev => prev.filter(p => p.connection_id !== connId));
      if (status === 'accepted') fetchConversations();
    } catch {}
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="messages-page">
      <motion.div variants={fadeUp} className="mb-6">
        <h1 className="font-heading text-2xl font-medium tracking-tight">Messages</h1>
        <p className="text-xs text-muted-foreground mt-1">Chat with your connections</p>
      </motion.div>

      <motion.div variants={fadeUp} className="flex h-[calc(100vh-180px)] rounded-xl border border-border overflow-hidden bg-card">
        {/* Left panel — conversation list */}
        <div className="w-80 border-r border-border flex flex-col flex-shrink-0">
          {/* Tabs */}
          <div className="flex border-b border-border px-1 py-1 gap-0.5">
            {[
              { id: 'messages', label: 'Messages', badge: totalUnread },
              { id: 'connections', label: 'Connections', badge: 0 },
              { id: 'requests', label: 'Requests', badge: pending.length },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setActiveConvo(null); }}
                className={`flex-1 py-2 px-2 text-[11px] font-medium rounded-md transition-colors relative ${
                  tab === t.id ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
                {t.badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 text-[8px] text-white flex items-center justify-center font-bold">
                    {t.badge > 9 ? '9+' : t.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {tab === 'messages' && conversations.map(c => (
              <button
                key={c.conversation_id}
                onClick={() => openConversation(c)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/60 transition-colors border-b border-border/30 ${
                  activeConvo?.conversation_id === c.conversation_id ? 'bg-secondary' : ''
                }`}
                data-testid={`convo-${c.conversation_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold flex-shrink-0 overflow-hidden">
                  {c.partner_picture ? (
                    <img src={c.partner_picture} alt="" className="w-9 h-9 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    c.partner_name?.[0]?.toUpperCase() || '?'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs truncate ${c.unread_count > 0 ? 'font-semibold' : 'font-medium'}`}>{c.partner_name}</span>
                    <span className="text-[9px] text-muted-foreground flex-shrink-0"><TimeAgo date={c.last_message_at} /></span>
                  </div>
                  <div className="flex items-center gap-1">
                    {c.last_sender_id === user?.user_id && <CheckCheck className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" strokeWidth={1.5} />}
                    <p className={`text-[10px] truncate ${c.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                      {c.last_message || 'Start a conversation'}
                    </p>
                  </div>
                </div>
                {c.unread_count > 0 && (
                  <span className="w-5 h-5 rounded-full bg-amber-500 text-[9px] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {c.unread_count}
                  </span>
                )}
              </button>
            ))}

            {tab === 'messages' && conversations.length === 0 && (
              <div className="text-center py-10 text-xs text-muted-foreground">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" strokeWidth={1.5} />
                No conversations yet
              </div>
            )}

            {tab === 'connections' && connections.map(c => (
              <button
                key={c.connection_id}
                onClick={() => startChat(c.user_id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/60 transition-colors border-b border-border/30"
                data-testid={`connection-${c.user_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold flex-shrink-0 overflow-hidden">
                  {c.picture ? (
                    <img src={c.picture} alt="" className="w-9 h-9 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    c.name?.[0]?.toUpperCase() || '?'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{c.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{c.role} · {c.location}</p>
                </div>
                <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 text-[10px] font-semibold tabular-nums">{c.trust_score}</span>
                <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
              </button>
            ))}

            {tab === 'connections' && connections.length === 0 && (
              <div className="text-center py-10 text-xs text-muted-foreground">
                <UserPlus className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" strokeWidth={1.5} />
                No connections yet. Send connection requests from the People page.
              </div>
            )}

            {tab === 'requests' && pending.map(p => (
              <div key={p.connection_id} className="px-4 py-3 border-b border-border/30" data-testid={`request-${p.connection_id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold flex-shrink-0 overflow-hidden">
                    {p.picture ? (
                      <img src={p.picture} alt="" className="w-9 h-9 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      p.name?.[0]?.toUpperCase() || '?'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.role}</p>
                  </div>
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 text-[10px] font-semibold">{p.trust_score}</span>
                </div>
                {p.message && <p className="text-[10px] text-muted-foreground mb-2 italic">"{p.message}"</p>}
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-[11px] flex-1" onClick={() => respondToRequest(p.connection_id, 'accepted')}>Accept</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={() => respondToRequest(p.connection_id, 'declined')}>Decline</Button>
                </div>
              </div>
            ))}

            {tab === 'requests' && pending.length === 0 && (
              <div className="text-center py-10 text-xs text-muted-foreground">No pending requests</div>
            )}
          </div>
        </div>

        {/* Right panel — chat */}
        <div className="flex-1 flex flex-col">
          {activeConvo ? (
            <>
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-border">
                <button onClick={() => setActiveConvo(null)} className="lg:hidden">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold overflow-hidden">
                  {activeConvo.partner_picture ? (
                    <img src={activeConvo.partner_picture} alt="" className="w-8 h-8 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    activeConvo.partner_name?.[0]?.toUpperCase() || '?'
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">{activeConvo.partner_name}</p>
                  <p className="text-[9px] text-muted-foreground font-mono">{activeConvo.partner_ainaa_id}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.map(m => (
                  <div key={m.message_id} className={`flex ${m.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed ${
                      m.sender_id === user?.user_id
                        ? 'bg-foreground text-background rounded-br-md'
                        : 'bg-secondary text-foreground rounded-bl-md'
                    }`}>
                      {m.content}
                      <div className={`flex items-center gap-1 mt-0.5 ${m.sender_id === user?.user_id ? 'justify-end' : ''}`}>
                        <span className={`text-[8px] ${m.sender_id === user?.user_id ? 'text-background/50' : 'text-muted-foreground/50'}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {m.sender_id === user?.user_id && (
                          m.read ? <CheckCheck className="w-2.5 h-2.5 text-blue-400" strokeWidth={2} /> : <Check className="w-2.5 h-2.5 text-background/40" strokeWidth={2} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              {/* Input */}
              <div className="px-4 py-3 border-t border-border">
                <form onSubmit={sendMessage} className="flex gap-2">
                  <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 h-10 px-4 rounded-full border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="message-input"
                  />
                  <Button type="submit" size="icon" className="h-10 w-10 rounded-full" disabled={!input.trim() || sending} data-testid="send-message-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/15" strokeWidth={1} />
                <p className="text-sm text-muted-foreground">Select a conversation</p>
                <p className="text-[10px] text-muted-foreground/60 mt-1">or start chatting with a connection</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
